import type { IContentPageBannerFields, Theme } from '@boss/types/b2b-b2c';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { BackgroundColorOptions, DisplayOptions, MainColor, TextColorOptions } from './constants';
import type { DisplayOptionValue } from './constants';

type Props = {
  id?: string;
  title: string;
  description?: React.ReactNode;
  imageUrl?: string | null;
  imageAlt?: string | null;
  mainColor?: IContentPageBannerFields['mainColor'];
  textColor?: IContentPageBannerFields['textColor'];
  callToAction?: React.ReactNode;
  type?: DisplayOptionValue;
  theme?: Theme;
  className?: string;
};

const displayTypeMap: Record<DisplayOptionValue, DisplayOptions> = {
  'Banner full page': DisplayOptions.FULL_PAGE,
  'Banner deals': DisplayOptions.DEALS,
  'Banner opleidingen': DisplayOptions.OPL,
  'Banner Quote': DisplayOptions.QUOTE,
};

const bannerVariants = cva('relative', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: '',
      [DisplayOptions.DEALS]: 'p-6 py-8 grid grid-cols-3 gap-4',
      [DisplayOptions.OPL]: 'grid grid-cols-10 gap-2',
      [DisplayOptions.QUOTE]: 'pt-20 px-4 pb-4 lg:pt-4 grid grid-cols-3 gap-4',
    },
    textColor: {
      Dark: '',
      Light: '',
    },
    theme: {
      'Colora: pink': '',
      'Colora: green': '',
      'Colora: blue': '',
      'Colora: grey': '',
      'Colora: beige': '',
      'B2B: Dark green': '',
      'B2B: Forest green': '',
      'B2B: Muted blue': '',
      'B2B: Muted red': '',
    },
    mainColor: {
      [MainColor.FOREST_GREEN]: '',
      [MainColor.DARK_GREEN]: '',
      [MainColor.MUTED_BLUE]: '',
      [MainColor.MUTED_RED]: '',
      [MainColor.SOFT_GREEN]: '',
      [MainColor.LUSH_GREEN]: '',
    },
  },
  compoundVariants: [
    { display: DisplayOptions.FULL_PAGE, theme: 'Colora: pink', className: '' },
    { display: DisplayOptions.DEALS, mainColor: MainColor.FOREST_GREEN, className: BackgroundColorOptions.GREEN },
    { display: DisplayOptions.DEALS, mainColor: MainColor.DARK_GREEN, className: BackgroundColorOptions.GREEN_DARK },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_BLUE, className: BackgroundColorOptions.BLUE_MUTED },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_RED, className: BackgroundColorOptions.RED },
    { display: DisplayOptions.DEALS, mainColor: MainColor.SOFT_GREEN, className: BackgroundColorOptions.GREEN_SOFT },
    { display: DisplayOptions.DEALS, mainColor: MainColor.LUSH_GREEN, className: BackgroundColorOptions.GREEN_LUSH },
    { display: DisplayOptions.OPL, mainColor: MainColor.FOREST_GREEN, className: BackgroundColorOptions.GREEN },
    { display: DisplayOptions.OPL, mainColor: MainColor.DARK_GREEN, className: BackgroundColorOptions.GREEN_DARK },
    { display: DisplayOptions.OPL, mainColor: MainColor.MUTED_BLUE, className: BackgroundColorOptions.BLUE_MUTED },
    { display: DisplayOptions.OPL, mainColor: MainColor.MUTED_RED, className: BackgroundColorOptions.RED },
    { display: DisplayOptions.OPL, mainColor: MainColor.SOFT_GREEN, className: BackgroundColorOptions.GREEN_SOFT },
    { display: DisplayOptions.OPL, mainColor: MainColor.LUSH_GREEN, className: BackgroundColorOptions.GREEN_LUSH },
    { textColor: 'Dark', display: DisplayOptions.FULL_PAGE, className: TextColorOptions.BLACK },
    { textColor: 'Light', display: DisplayOptions.FULL_PAGE, className: TextColorOptions.WHITE },
  ],
  defaultVariants: {
    display: DisplayOptions.FULL_PAGE,
    textColor: 'Dark',
    mainColor: MainColor.DARK_GREEN,
  },
});

const textClass = cva('flex flex-col justify-center', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: 'relative z-10 max-w-1/2 py-15',
      [DisplayOptions.DEALS]: 'col-span-2',
      [DisplayOptions.OPL]:
        'col-span-10 md:col-span-6 pt-6 md:pt-8 px-10 md:px-16 order-2 md:order-1 justify-start self-start',
      [DisplayOptions.QUOTE]: 'col-span-3 md:col-span-2 order-2 md:order-1',
    },
  },
});

const ctaStyleVariant = cva('', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: 'relative z-10 max-w-1/2 pb-15 mt-4',
      [DisplayOptions.DEALS]: 'col-span-3 mt-0 flex flex-col gap-4',
      [DisplayOptions.OPL]: 'mt-4 col-span-12 md:col-span-6 pb-10 md:pb-10 px-10 md:px-16 order-3',
      [DisplayOptions.QUOTE]: 'mt-4 col-span-3',
    },
  },
});

const headingStyleVariant = cva('', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: '',
      [DisplayOptions.DEALS]: 'font-brandon mb-0',
      [DisplayOptions.OPL]: '',
      [DisplayOptions.QUOTE]: 'h2 font-bold',
    },
    textColor: {
      Dark: '',
      Light: '',
    },
    mainColor: {
      [MainColor.FOREST_GREEN]: '',
      [MainColor.DARK_GREEN]: '',
      [MainColor.MUTED_BLUE]: '',
      [MainColor.MUTED_RED]: '',
      [MainColor.SOFT_GREEN]: '',
      [MainColor.LUSH_GREEN]: '',
    },
  },
  compoundVariants: [
    { display: DisplayOptions.FULL_PAGE, textColor: 'Dark', className: TextColorOptions.BLACK },
    { display: DisplayOptions.FULL_PAGE, textColor: 'Light', className: TextColorOptions.WHITE },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.FOREST_GREEN, className: TextColorOptions.GREEN },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.DARK_GREEN, className: TextColorOptions.GREEN_DARK },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.MUTED_BLUE, className: TextColorOptions.BLUE_SILVER },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.MUTED_RED, className: TextColorOptions.RED },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.SOFT_GREEN, className: TextColorOptions.GREEN_SOFT },
    { display: DisplayOptions.QUOTE, mainColor: MainColor.LUSH_GREEN, className: TextColorOptions.GREEN_LUSH },
    { display: DisplayOptions.DEALS, mainColor: MainColor.FOREST_GREEN, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.DARK_GREEN, className: TextColorOptions.GREEN_LUSH },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_BLUE, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_RED, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.SOFT_GREEN, className: TextColorOptions.GREEN },
    { display: DisplayOptions.DEALS, mainColor: MainColor.LUSH_GREEN, className: TextColorOptions.GREEN_DARK },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.FOREST_GREEN,
      className: TextColorOptions.WHITE,
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.DARK_GREEN,
      className: TextColorOptions.GREEN_LUSH, // darker green for better contrast with background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.MUTED_BLUE,
      className: TextColorOptions.WHITE, // white for better contrast with muted blue background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.MUTED_RED,
      className: TextColorOptions.WHITE, // white for better contrast with muted red background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.SOFT_GREEN,
      className: TextColorOptions.GREEN, // use green for softer contrast with soft green background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.LUSH_GREEN,
      className: TextColorOptions.GREEN_DARK, // darker green for better contrast with lush green background
    },
  ],
});

const subTextStyleVariant = cva('', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: '',
      [DisplayOptions.DEALS]: 'mt-0',
      [DisplayOptions.OPL]: '',
      [DisplayOptions.QUOTE]: '',
    },
    textColor: {
      Dark: '',
      Light: '',
    },
    mainColor: {
      [MainColor.FOREST_GREEN]: TextColorOptions.GREEN,
      [MainColor.DARK_GREEN]: TextColorOptions.GREEN_DARK,
      [MainColor.MUTED_BLUE]: TextColorOptions.BLUE_MUTED,
      [MainColor.MUTED_RED]: TextColorOptions.RED,
      [MainColor.SOFT_GREEN]: TextColorOptions.GREEN_SOFT,
      [MainColor.LUSH_GREEN]: TextColorOptions.GREEN_LUSH,
    },
  },
  compoundVariants: [
    { display: DisplayOptions.FULL_PAGE, textColor: 'Dark', className: TextColorOptions.BLACK },
    { display: DisplayOptions.FULL_PAGE, textColor: 'Light', className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.FOREST_GREEN, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.DARK_GREEN, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_BLUE, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.MUTED_RED, className: TextColorOptions.WHITE },
    { display: DisplayOptions.DEALS, mainColor: MainColor.SOFT_GREEN, className: TextColorOptions.GREEN },
    { display: DisplayOptions.DEALS, mainColor: MainColor.LUSH_GREEN, className: TextColorOptions.GREEN_DARK },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.FOREST_GREEN,
      className: TextColorOptions.WHITE,
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.DARK_GREEN,
      className: TextColorOptions.WHITE, // darker green for better contrast with background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.MUTED_BLUE,
      className: TextColorOptions.WHITE, // white for better contrast with muted blue background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.MUTED_RED,
      className: TextColorOptions.WHITE, // white for better contrast with muted red background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.SOFT_GREEN,
      className: TextColorOptions.BLACK, // use green for softer contrast with soft green background
    },
    {
      display: DisplayOptions.OPL,
      mainColor: MainColor.LUSH_GREEN,
      className: TextColorOptions.GREEN_DARK, // darker green for better contrast with lush green background
    },
  ],
});

const iconStyles = cva('', {
  variants: {
    display: {
      [DisplayOptions.QUOTE]: 'order-1 md:order-2',
      [DisplayOptions.DEALS]: '',
      [DisplayOptions.OPL]: '',
      [DisplayOptions.FULL_PAGE]: '',
    },
    mainColor: {
      [MainColor.FOREST_GREEN]: '',
      [MainColor.DARK_GREEN]: '',
      [MainColor.MUTED_BLUE]: '',
      [MainColor.MUTED_RED]: '',
      [MainColor.SOFT_GREEN]: '',
      [MainColor.LUSH_GREEN]: '',
    },
    hasImage: {
      true: 'absolute right-0 bottom-0 w-30 h-20',
      false: 'w-auto md:h-30 ml-auto',
    },
  },
  compoundVariants: [
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.FOREST_GREEN,
      className: TextColorOptions.GREEN,
    },
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.DARK_GREEN,
      className: TextColorOptions.GREEN_DARK,
    },
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.MUTED_BLUE,
      className: TextColorOptions.BLUE_SILVER,
    },
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.MUTED_RED,
      className: TextColorOptions.RED_SALMON,
    },
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.SOFT_GREEN,
      className: TextColorOptions.GREEN_SOFT,
    },
    {
      display: DisplayOptions.QUOTE,
      mainColor: MainColor.LUSH_GREEN,
      className: TextColorOptions.GREEN_LUSH,
    },
    {
      display: DisplayOptions.QUOTE,
      hasImage: true,
      className: 'top-0 left-4 w-[6rem] md:hidden',
    },
    {
      display: DisplayOptions.QUOTE,
      hasImage: false,
      className: 'absolute left-4 top-0 !w-[6rem] md:relative md:!w-auto md:left-auto md:top-auto',
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.FOREST_GREEN,
      className: TextColorOptions.GREEN_LUSH,
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.DARK_GREEN,
      className: TextColorOptions.GREEN_LUSH,
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.LUSH_GREEN,
      className: TextColorOptions.GREEN_DARK,
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.SOFT_GREEN,
      className: TextColorOptions.GREEN,
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.MUTED_BLUE,
      className: TextColorOptions.BLUE_SILVER,
    },
    {
      display: DisplayOptions.DEALS,
      mainColor: MainColor.MUTED_RED,
      className: TextColorOptions.RED_SALMON,
    },
  ],
});

const ImageStyles = cva('', {
  variants: {
    display: {
      [DisplayOptions.FULL_PAGE]: 'absolute left-[50%] top-0 z-0 bottom-0 -translate-x-1/2 w-[100vw] overflow-hidden',
      [DisplayOptions.DEALS]: 'col-span-1 relative overflow-hidden row-span-2',
      [DisplayOptions.OPL]: 'col-span-12 md:col-span-4 order-1 md:order-2 relative overflow-hidden md:row-span-2',
      [DisplayOptions.QUOTE]: 'order-1 md:order-2 col-span-1 relative overflow-hidden',
    },
  },
});

const InnerImageStyles = cva('inset-0 h-full w-full object-contain md:absolute', {
  variants: {
    display: {
      [DisplayOptions.QUOTE]: 'h-auto max-h-full w-auto max-w-full object-contain"',
      [DisplayOptions.FULL_PAGE]: 'inset-0 h-full w-full object-cover md:absolute',
      [DisplayOptions.DEALS]: '',
      [DisplayOptions.OPL]: 'object-cover',
    },
  },
});

/**
 * Pinwheel decoration matching the Boss/Colora brand logo:
 * four squares each with a concave quarter-circle cut from the inner corner,
 * arranged in a 2×2 grid with a small gap at the centre.
 * Uses fill="currentColor" so colour is inherited via CSS `color`.
 */
const PinwheelDecoration = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="currentColor"
    viewBox="0 0 382 190"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Single pinwheel shape, reused twice via <use> */}
    <defs>
      <g id="pinwheel">
        <path d="M167.979095,9.402396 C172.138245,9.408692 175.801666,9.412025 179.465057,9.425980 C179.795731,9.427238 180.126144,9.497005 180.456436,9.537064 C184.753052,44.122799 151.195023,94.890724 96.804848,94.244110 C96.804848,66.228790 96.804848,38.204651 96.804848,9.402305 C120.346352,9.402305 143.914856,9.402305 167.979095,9.402396 z" />
        <path d="M70.873146,37.134750 C85.457458,53.015926 94.122910,75.493629 92.909065,94.478050 C65.067253,94.478050 37.196022,94.478050 8.864706,94.478050 C8.864706,66.479591 8.864706,38.316757 8.864706,9.585742 C33.378719,9.897542 53.845200,19.146528 70.873146,37.134750 z" />
        <path d="M142.000000,98.001984 C155.458832,98.001984 168.417664,98.001984 181.686523,98.001984 C181.686523,126.278755 181.686523,154.310806 181.686523,182.351318 C143.422714,185.643036 96.842155,150.320007 97.274445,98.001984 C112.006439,98.001984 126.753220,98.001984 142.000000,98.001984 z" />
        <path d="M39.348511,117.321159 C55.062119,104.710365 72.665543,98.408241 92.826477,98.007828 C92.826477,126.321686 92.826477,154.350266 92.826477,182.689133 C64.906822,182.689133 36.880283,182.689133 8.444346,182.689133 C9.002842,156.243546 19.176109,134.634384 39.348511,117.321159 z" />
      </g>
    </defs>
    {/* First pinwheel */}
    <use href="#pinwheel" />
    {/* Second pinwheel — 186 wide + 10 gap */}
    <use href="#pinwheel" transform="translate(196, 0)" />
  </svg>
);

type BannerDescriptionProps = {
  description?: React.ReactNode;
  displayType: DisplayOptions;
  textColor?: IContentPageBannerFields['textColor'];
  color?: MainColor;
  marginClassName: string;
  richTextInheritColorClass: string;
};

const BannerDescription = ({
  description,
  displayType,
  textColor,
  color,
  marginClassName,
  richTextInheritColorClass,
}: BannerDescriptionProps) => {
  if (!description) {
    return null;
  }

  return (
    <div
      className={twMerge(
        subTextStyleVariant({ display: displayType, textColor, mainColor: color }),
        marginClassName,
        richTextInheritColorClass,
      )}
    >
      {description}
    </div>
  );
};

type BannerTextDescriptionProps = {
  description?: React.ReactNode;
  displayType: DisplayOptions;
  textColor?: IContentPageBannerFields['textColor'];
  color?: MainColor;
  richTextInheritColorClass: string;
};

const BannerTextDescription = ({
  description,
  displayType,
  textColor,
  color,
  richTextInheritColorClass,
}: BannerTextDescriptionProps) => {
  if (displayType === DisplayOptions.DEALS) {
    return null;
  }

  return (
    <BannerDescription
      color={color}
      description={description}
      displayType={displayType}
      marginClassName="mb-0"
      richTextInheritColorClass={richTextInheritColorClass}
      textColor={textColor}
    />
  );
};

type BannerCtaProps = {
  description?: React.ReactNode;
  callToAction?: React.ReactNode;
  displayType: DisplayOptions;
  textColor?: IContentPageBannerFields['textColor'];
  color?: MainColor;
  richTextInheritColorClass: string;
};

const BannerCta = ({
  description,
  callToAction,
  displayType,
  textColor,
  color,
  richTextInheritColorClass,
}: BannerCtaProps) => {
  const hasDealsDescription = displayType === DisplayOptions.DEALS && Boolean(description);

  if (!callToAction && !hasDealsDescription) {
    return null;
  }

  return (
    <div className={ctaStyleVariant({ display: displayType })}>
      {hasDealsDescription ? (
        <BannerDescription
          color={color}
          description={description}
          displayType={displayType}
          marginClassName=""
          richTextInheritColorClass={richTextInheritColorClass}
          textColor={textColor}
        />
      ) : null}
      {callToAction}
    </div>
  );
};

const ContentPageBanner = ({
  id,
  title,
  description,
  imageUrl,
  imageAlt,
  textColor,
  mainColor,
  callToAction,
  type = DisplayOptions.FULL_PAGE,
  theme,
  className,
}: Props) => {
  const color = mainColor as MainColor | undefined;
  const displayType = displayTypeMap[type ?? DisplayOptions.FULL_PAGE];

  const wrapperClass = twMerge(
    bannerVariants({
      display: displayType,
      textColor: textColor ?? undefined,
      theme: theme ?? undefined,
      mainColor: color,
    }),
    className,
  );

  // Use token-based half-opacity background when mainColor is soft green
  const extraBgClass =
    color === MainColor.SOFT_GREEN && displayType === DisplayOptions.FULL_PAGE
      ? 'bg-greenSoft50 before:bg-greenSoft50 after:bg-greenSoft50'
      : '';
  const adjustedWrapperClass = twMerge(wrapperClass, extraBgClass);

  const colorClass = headingStyleVariant({ display: displayType, textColor, mainColor: color });
  const richTextInheritColorClass =
    '[&_a]:text-inherit [&_blockquote]:text-inherit [&_em]:text-inherit [&_h1]:text-inherit [&_h2]:text-inherit [&_h3]:text-inherit [&_h4]:text-inherit [&_h5]:text-inherit [&_h6]:text-inherit [&_li]:text-inherit [&_ol]:text-inherit [&_p:first-of-type]:mt-0 [&_p:last-of-type]:mb-0 [&_p]:text-inherit [&_span]:text-inherit [&_strong]:text-inherit [&_ul]:text-inherit';

  return (
    <div className={adjustedWrapperClass} data-testid="contentPageBanner" id={id}>
      <div className={textClass({ display: displayType })}>
        {displayType === DisplayOptions.FULL_PAGE ? (
          <h1 className={colorClass}>{title}</h1>
        ) : (
          <h2 className={colorClass}>{title}</h2>
        )}
        {/* <h2 className={colorClass}>{title}</h2> */}
        <BannerTextDescription
          color={color}
          description={description}
          displayType={displayType}
          richTextInheritColorClass={richTextInheritColorClass}
          textColor={textColor}
        />
      </div>

      {imageUrl ? (
        <div className={ImageStyles({ display: displayType })}>
          <img alt={imageAlt ?? title} className={InnerImageStyles({ display: displayType })} src={imageUrl} />
        </div>
      ) : null}
      {(displayType === DisplayOptions.QUOTE ||
        displayType === DisplayOptions.DEALS ||
        displayType === DisplayOptions.OPL) &&
      !imageUrl ? (
        <PinwheelDecoration className={iconStyles({ display: displayType, mainColor: color, hasImage: !!imageUrl })} />
      ) : null}

      <BannerCta
        callToAction={callToAction}
        color={color}
        description={description}
        displayType={displayType}
        richTextInheritColorClass={richTextInheritColorClass}
        textColor={textColor}
      />
    </div>
  );
};

export default ContentPageBanner;
